import { Autocomplete, TextField } from "@mui/material";
import { Axios } from "api/axios";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import PageContainer from "components/PageContainer/PageContainer";
// import { autocompleteDropdownStyles } from "Constants/Style";
import { autocompleteDropdownStyles } from "Constants/Style";
import { City, Country, State } from "country-state-city";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

// Common input style for Form.Control to match Autocomplete styling
const commonInputStyle = {
  fontSize: "0.8rem",
  minHeight: 38,
  padding: "0.375rem 0.75rem",
};

const commonLabelStyle = {
  fontSize: "0.8rem",
  marginBottom: 6,
  marginTop: 12,
};

interface FormValues {
  warehouse_name: string;
  warehouse_type: string;
  address: string;
  country: any | null;
  state: any | null;
  city: any | null | "";
  pincode: string;
  remark: string;
}

const WarehouseMasterForm: React.FC = () => {
  document.title = "Warehouse form";
  const { id } = useParams();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [countries, setCountries] = useState<any>([]);
  const [states, setStates] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [formLoding, setFormLoading] = useState<boolean>(false);

  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);

    // Auto-select India when creating a new warehouse (not in edit mode)
    if (!id && countryList.length > 0 && !state?.country) {
      const india = countryList.find(
        (c: any) => c.name === "India" || c.isoCode === "IN"
      );
      if (india) {
        formik.setFieldValue("country", india);
        // Load states for India
        const indiaStates = State.getStatesOfCountry(india.isoCode);
        setStates(indiaStates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      warehouse_name: state ? state?.warehouse_name : null,
      warehouse_type: state ? state?.warehouse_type : null,
      address: state ? state?.address : "",
      city: state ? { name: state?.city } : null,
      state: null,
      country: state ? { name: state?.country } : null,
      pincode: state ? state?.pincode : "",
      remark: state ? state?.remark : "",
    },
    validationSchema: Yup.object({
      warehouse_name: Yup.string().required("Warehouse Name is required"),
      warehouse_type: Yup.string().required("Warehouse Type is required"),
      address: Yup.string().required("Address is required"),
      country: Yup.mixed().required("Country is required"),
      state: Yup.mixed().required("State is required"),
      city: Yup.mixed().required("City is required"),
      pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^[1-9][0-9]{5}$/, "Enter a valid 6-digit pincode"),
    }),
    onSubmit: async (values) => {
      const payload = {
        ...values,
        country: values?.country?.name,
        state: values?.state?.name,
        city: values?.city?.name,
      };
      try {
        setFormLoading(true);
        if (id) {
          await Axios.put(
            `/master/warehouse/update/${state?.warehouse_id}/`,
            payload
          );
          toast.success("Warehouse Updated Successfully");
        } else {
          await Axios.post("/master/warehouse/create", payload);
          toast.success("Warehouse Created Successfully");
        }
        navigate("/warehouse-list", { state: { clearState: !id } });
      } catch (err: any) {
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } finally {
        setFormLoading(false);
      }
    },
  });

  useEffect(() => {
    // Load states and cities for edit mode
    if (id && state?.country && countries.length > 0) {
      const selectedCountry = countries.find(
        (c: any) => c?.name === state.country
      );
      if (selectedCountry) {
        formik.setFieldValue("country", selectedCountry);
        const countryStates = State.getStatesOfCountry(selectedCountry.isoCode);
        setStates(countryStates);
        const selectedState = countryStates.find(
          (s: any) => s?.name === state.state
        );
        if (selectedState) {
          formik.setFieldValue("state", selectedState);
          const cities = City.getCitiesOfState(
            selectedCountry.isoCode,
            selectedState.isoCode
          );
          setCities(cities ?? []);
          if (state?.city) {
            const selectedCity = cities.find(
              (c: any) => c?.name === state.city
            );
            if (selectedCity) {
              formik.setFieldValue("city", selectedCity);
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, state?.country, state?.state, state?.city, countries]);
  return (
    <PageContainer>
      <FormHeader
        title={`${id ? "Edit" : "Add"} Warehouse`}
        firstLabel="Warehouse"
        secondLabel={id ? "Edit Warehouse" : "Add Warehouse"}
        handleArrowClick={() => navigate("/warehouse-list")}
      />

      <Form autoComplete="off" onSubmit={formik.handleSubmit} noValidate>
        <Row>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              Warehouse Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="warehouse_name"
              placeholder="Enter Warehouse Name"
              value={formik.values.warehouse_name}
              onChange={(e) => {
                const value = e.target.value;
                formik.setFieldValue("warehouse_name", value);
                // if (/^[a-zA-Z\s]*$/g.test(value)) {
                //     formik.setFieldValue("warehouse_name", value);
                // }
              }}
              onBlur={formik.handleBlur}
              isInvalid={
                !!formik.errors.warehouse_name && formik.touched.warehouse_name
              }
              style={commonInputStyle}
              className="form-control-placeholder"
            />
            <Form.Control.Feedback
              type="invalid"
              style={{ color: "#dc3545" }}
            >
              {formik.touched.warehouse_name && formik.errors.warehouse_name}
            </Form.Control.Feedback>
          </Col>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              Warehouse Type <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              disablePortal
              size="small"
              options={["Main", "Retail"]}
              value={formik.values.warehouse_type}
              onChange={(_, val) => formik.setFieldValue("warehouse_type", val)}
              onBlur={() => formik.setFieldTouched("warehouse_type", true)}
              getOptionLabel={(option) => (option ? option : "")}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt: any) => opt?.toString() || ""
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Warehouse Type"
                  error={
                    formik.touched.warehouse_type &&
                    Boolean(formik.errors.warehouse_type)
                  }
                  helperText={
                    formik.touched.warehouse_type &&
                    formik.errors.warehouse_type ? (
                      <span>
                        {(() => {
                          const error: any = formik.errors.warehouse_type;
                          if (typeof error === "string") return error as string;
                          if (Array.isArray(error)) return (error[0] || "") as string;
                          return String(error || "") as string;
                        })()}
                      </span>
                    ) : null
                  }
                  fullWidth
                  size="small"
                  autoComplete="off"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: commonInputStyle,
                  }}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    style: commonLabelStyle,
                  }}
                />
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="address"
              placeholder="Enter Address"
              value={formik.values.address}
              onChange={(e) => {
                const value = e.target.value;
                formik.setFieldValue("address", value);
              }}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.address && formik.touched.address}
              style={commonInputStyle}
              className="form-control-placeholder"
            />
            <Form.Control.Feedback
              type="invalid"
              style={{ color: "#dc3545" }}
            >
              {formik.touched.address && formik.errors.address}
            </Form.Control.Feedback>
          </Col>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              Country <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              disablePortal
              size="small"
              options={countries || []}
              getOptionLabel={(option: any) =>
                option ? option?.name : ""
              }
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              value={formik.values.country}
              onChange={(_, val) => {
                formik.setFieldValue("country", val);
                formik.setFieldValue("state", null);
                formik.setFieldValue("city", null);
                if (val) {
                  const newStates = State.getStatesOfCountry(val?.isoCode);
                  setStates(newStates);
                  setCities([]);
                }
              }}
              onBlur={() => formik.setFieldTouched("country", true)}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt: any) => opt?.name || ""
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Country"
                  error={
                    formik.touched.country && Boolean(formik.errors.country)
                  }
                  helperText={
                    formik.touched.country &&
                    formik.errors.country ? (
                      <span>
                        {(() => {
                          const error: any = formik.errors.country;
                          if (typeof error === "string") return error as string;
                          if (Array.isArray(error)) return (error[0] || "") as string;
                          return String(error || "") as string;
                        })()}
                      </span>
                    ) : null
                  }
                  fullWidth
                  size="small"
                  autoComplete="off"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: commonInputStyle,
                  }}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    style: commonLabelStyle,
                  }}
                />
              )}
            />
          </Col>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              State <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              disablePortal
              size="small"
              options={states || []}
              getOptionLabel={(option: any) =>
                option ? option?.name : ""
              }
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              value={formik.values.state}
              onChange={(_, val) => {
                formik.setFieldValue("state", val);
                formik.setFieldValue("city", null);
                if (formik?.values?.country && val) {
                  const newCities = City.getCitiesOfState(
                    formik?.values?.country?.isoCode ?? "",
                    val.isoCode
                  );
                  setCities(newCities || []);
                }
              }}
              onBlur={() => formik.setFieldTouched("state", true)}
              disabled={!formik.values.country}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt: any) => opt?.name || ""
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    !formik.values.country
                      ? "Select Country First"
                      : "Select State"
                  }
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={
                    formik.touched.state &&
                    formik.errors.state ? (
                      <span>
                        {(() => {
                          const error: any = formik.errors.state;
                          if (typeof error === "string") return error as string;
                          if (Array.isArray(error)) return (error[0] || "") as string;
                          return String(error || "") as string;
                        })()}
                      </span>
                    ) : null
                  }
                  fullWidth
                  size="small"
                  autoComplete="off"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: commonInputStyle,
                  }}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    style: commonLabelStyle,
                  }}
                />
              )}
            />
          </Col>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              City <span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              disablePortal
              size="small"
              options={cities || []}
              getOptionLabel={(option: any) =>
                option ? option?.name : ""
              }
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              value={formik.values.city}
              onChange={(_, val) => {
                formik.setFieldValue("city", val);
              }}
              onBlur={() => formik.setFieldTouched("city", true)}
              disabled={!formik.values.state}
              renderOption={(props, option) =>
                autocompleteDropdownStyles.renderOption(
                  props,
                  option,
                  (opt: any) => opt?.name || ""
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    !formik.values.state ? "Select State First" : "Select City"
                  }
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={
                    formik.touched.city &&
                    formik.errors.city ? (
                      <span>
                        {(() => {
                          const error: any = formik.errors.city;
                          if (typeof error === "string") return error as string;
                          if (Array.isArray(error)) return (error[0] || "") as string;
                          return String(error || "") as string;
                        })()}
                      </span>
                    ) : null
                  }
                  fullWidth
                  size="small"
                  autoComplete="off"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: commonInputStyle,
                  }}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    style: commonLabelStyle,
                  }}
                />
              )}
            />
          </Col>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>
              Pincode <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={6}
              name="pincode"
              placeholder="Enter Pincode"
              value={formik.values.pincode}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9\s]*$/g.test(value)) {
                  formik.setFieldValue("pincode", value);
                }
              }}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.pincode && formik.touched.pincode}
              style={commonInputStyle}
              className="form-control-placeholder"
            />
            <Form.Control.Feedback
              type="invalid"
              style={{ color: "#dc3545" }}
            >
              {formik.touched.pincode && formik.errors.pincode}
            </Form.Control.Feedback>
          </Col>
          <Col lg={3} className="mb-3">
            <Form.Label className="fw-semibold" style={commonLabelStyle}>Remark</Form.Label>
            <Form.Control
              type="text"
              name="remark"
              placeholder="Enter Remark"
              value={formik.values.remark}
              onChange={(e) => {
                const value = e.target.value;
                formik.setFieldValue("remark", value);
              }}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.errors.remark && formik.touched.remark}
              style={commonInputStyle}
              className="form-control-placeholder"
            />
            <Form.Control.Feedback
              type="invalid"
              style={{ color: "#dc3545" }}
            >
              {formik.touched.remark && formik.errors.remark}
            </Form.Control.Feedback>
          </Col>
        </Row>
        <div className="d-flex justify-content-center gap-3 py-3">
          {/* <Button
                    variant="light"
                    onClick={() => navigate(-1)}
                    style={{
                    backgroundColor: "white",
                    border: "1px solid #ced4da",
                    color: "#212529",
                    }}
                >
                    Cancel
                </Button> */}
          <CancelBackButton to="/warehouse-list" />
          {/* <Button
            type="submit"
            style={{
              background: "#3F7DDA",
              color: "#FFF",
              minWidth: "70px",
            }}
          >
            {!formLoding ? (
              id ? (
                "Update"
              ) : (
                "Submit"
              )
            ) : (
              <Spinner animation="border" size="sm" />
            )}
          </Button> */}
          <SubmitButton isSubmitting={formLoding} isEdit={!!id} />
        </div>
      </Form>
    </PageContainer>
  );
};

export default WarehouseMasterForm;
